import { ThreadStatusCountsMap } from "@ui/agent/components/agent/api/chat-api";
import { useTranslation } from "react-i18next";

import Button from "@/lib/components/ui/Button";
import { ThreadStatusIcon } from "@/lib/components/ui/ThreadStatusIcon/ThreadStatusIcon";
import { THREAD_STATUS_LABEL_KEYS, ThreadStatus } from "@/lib/types/Thread";

import styles from "./ThreadStatusCounts.module.scss";

const STATUSES = [
  ThreadStatus.InputRequired,
  ThreadStatus.Error,
  ThreadStatus.InProgress,
  ThreadStatus.Ready,
  ThreadStatus.Completed,
  ThreadStatus.Cancelled,
];

type ThreadStatusCountsProps = {
  /** Undefined while the first thread list response is loading. */
  counts: ThreadStatusCountsMap | undefined;
  isError?: boolean;
  statuses?: readonly ThreadStatus[];
  selectedStatus?: ThreadStatus | "";
  onStatusChange?: (status: ThreadStatus | "") => void;
  dataTest: string;
};

export const ThreadStatusCounts = ({
  counts,
  isError = false,
  statuses = STATUSES,
  selectedStatus,
  onStatusChange,
  dataTest,
}: ThreadStatusCountsProps): JSX.Element => {
  const { t } = useTranslation("translation");
  const isLoading = counts === undefined && !isError;

  return (
    <ul
      className={styles.statuses}
      aria-label={isError ? t("somethingWentWrongTryAgain") : t("status")}
      aria-busy={isLoading}
      data-test={dataTest}
    >
      {statuses.map((status) => {
        const count = counts?.[status] ?? (counts ? 0 : undefined);
        const label = `${t(THREAD_STATUS_LABEL_KEYS[status])}: ${count ?? "—"}`;
        const testKey = `${dataTest}-${status.replace(/_/g, "-")}`;
        const content = (
          <span className={styles.content}>
            <ThreadStatusIcon status={status} />
            <span className={styles.value}>{count ?? "—"}</span>
          </span>
        );

        return (
          <li key={status} className={styles.status}>
            {onStatusChange ? (
              <Button
                type="button"
                size="s"
                className={styles.button}
                variant={selectedStatus === status ? "primary" : "secondary"}
                aria-label={label}
                aria-pressed={selectedStatus === status}
                onClick={() =>
                  onStatusChange(selectedStatus === status ? "" : status)
                }
                data-test={testKey}
              >
                {content}
              </Button>
            ) : (
              <span
                className={styles.count}
                aria-label={label}
                data-test={testKey}
              >
                {content}
              </span>
            )}
          </li>
        );
      })}
    </ul>
  );
};
